import { ChangeDetectionStrategy, Component, inject, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { AuthService } from './auth.service';
import { Header } from './header';
import { SwipeTabSyncDirective } from './directives/swipe-tab-sync.directive';

export type AboutMainTab = 'about' | 'limiteds' | 'trusts';

interface MainTabOption {
  id: AboutMainTab;
  label: string;
  icon: string;
  badge: string;
}

interface ServiceDesc {
  title: string;
  icon: string;
  desc: string;
}

interface ServiceEntity {
  id: string;
  name: string;
  tagline: string;
  color: string;
  status: 'active' | 'in-process';
  icon: string;
  services: ServiceDesc[];
}

const KICGLE_SERVICES_DATA: { limiteds: ServiceEntity[]; trusts: ServiceEntity[] } = {
  limiteds: [
    {
      id: 'kicgle-private-limited',
      name: 'Kicgle Private Limited',
      tagline: 'Deep-Tech, Autonomous Systems & Consumer Hardware Ecosystem',
      color: 'ios-red',
      status: 'active',
      icon: 'rocket_launch',
      services: [
        { icon: 'rocket_launch', title: 'Deep Space & Rocketry', desc: 'Advanced aerospace research, orbital satellite constellations, and planetary exploration technologies.' },
        { icon: 'memory', title: 'Quantum AI & OS Ecosystem', desc: 'The singular Kicgle OS, artificial super-intelligence kernels, and infinite cloud architecture.' },
        { icon: 'search', title: 'Universal Search & Identity', desc: 'Hyper-accurate multi-modal search engines and the global Kicgle Digital Identity credential.' },
        { icon: 'favorite', title: 'Global Matrimony & Digital Matchmaking', desc: 'Advanced, verified, and intuitive platforms for lifelong partner discovery and wedding logistics.' },
        { icon: 'shopping_bag', title: 'FMCG & Consumer Products', desc: 'High-quality, everyday essential goods scaled for global retail dominance and sustainable manufacturing.' },
        { icon: 'bolt', title: 'EV Logistics & Autonomous Mobility', desc: 'Next-generation electric vehicle fleets, autonomous robotics, and intelligent green transit networks.' },
        { icon: 'dns', title: 'Semiconductor Manufacturing', desc: 'In-house high-yield fabrication of ultra-fast nano-chips powering frontier computing hardware.' },
        { icon: 'lightbulb', title: 'Future Tech Incubation', desc: 'Seed funding and rapid acceleration of world-changing robotics, cybernetics, and biotech innovations.' }
      ]
    },
    {
      id: 'kicgle-bank-limited',
      name: 'Kicgle Bank Limited',
      tagline: 'Next-Generation Borderless Digital Banking & Sovereign Capital',
      color: 'ios-green',
      status: 'in-process',
      icon: 'account_balance',
      services: [
        { icon: 'public', title: 'Borderless Digital Banking', desc: 'Zero-latency international wealth transfers, multi-currency accounts, and zero-fee transactions.' },
        { icon: 'payments', title: 'Dynamic Corporate & Agri Loans', desc: 'Scalable credit lines designed specifically for agile tech startups and global agricultural producers.' },
        { icon: 'gpp_good', title: 'Secured Crypto & Asset Vaults', desc: 'Military-grade digital asset custody, cold-storage security, and blockchain-backed equity reserves.' },
        { icon: 'credit_card', title: 'Micro-Finance & Retail Credit', desc: 'Instant algorithmic credit scoring, micro-lending, and dynamic personal financial planning.' },
        { icon: 'attach_money', title: 'Global Forex & Trade Finance', desc: 'Fluid international currency exchange, import-export underwriting, and sovereign bond facilities.' },
        { icon: 'school', title: 'Zero-Interest Student Capital', desc: 'Specialized educational loan structures offering zero interest to empower global students.' }
      ]
    },
    {
      id: 'kicgle-public-limited',
      name: 'Kicgle Public Limited',
      tagline: 'Public Capitalization, Mega-Infrastructure & Universal Insurance',
      color: 'ios-pink',
      status: 'in-process',
      icon: 'domain',
      services: [
        { icon: 'local_shipping', title: 'Universal Auto Insurance', desc: 'Instant automated claim settlement for personal vehicles, autonomous logistics, and transit fleets.' },
        { icon: 'health_and_safety', title: 'Omnilateral Life & Health Covering', desc: 'Uncompromising medical coverage and life protection structures accessible to all citizens.' },
        { icon: 'trending_up', title: 'Global IPO & Public Offerings', desc: 'Comprehensive capitalization strategies, secondary market listing, and shareholder value governance.' },
        { icon: 'domain', title: 'Mega-Project Sovereign Funding', desc: 'Strategic capital deployment for futuristic smart cities, high-speed rail, and clean energy grids.' },
        { icon: 'warning', title: 'Deep-Tech Asset Indemnity', desc: 'Underwriting algorithms tailored specifically for space missions, quantum compute servers, and enterprise risks.' }
      ]
    }
  ],
  trusts: [
    {
      id: 'kicgle-organization-trust',
      name: 'Kicgle Organization',
      tagline: 'Universal Humanitarian Welfare, Free Healthcare & Earth Restoration',
      color: 'ios-blue',
      status: 'active',
      icon: 'volunteer_activism',
      services: [
        { icon: 'favorite', title: 'Global Free Healthcare', desc: 'Zero-cost state-of-the-art hospitals, mobile telemedicine pods, and rapid emergency aid worldwide.' },
        { icon: 'public', title: 'Ecosystem & Wildlife Preservation', desc: 'Aggressive habitat protection for endangered species, reforestation, and oceanic cleanup initiatives.' },
        { icon: 'menu_book', title: '100% Free Education Grids', desc: 'Building open-access STEM academies and tech-enabled learning centers in marginalized communities.' },
        { icon: 'healing', title: 'Disaster Rapid Mobilization', desc: 'Immediate distribution of high-protein food rations, shelter kits, and critical medical relief during crises.' },
        { icon: 'auto_awesome', title: 'Global Clean Water Initiative', desc: 'Engineering and maintaining atmospheric water generators and sustainable solar water purification plants.' },
        { icon: 'light_mode', title: 'Zero-Carbon Organic Agriculture', desc: 'Promoting regenerative agricultural practices, community seed banks, and chemical-free soil restoration.' }
      ]
    },
    {
      id: 'kicgle-god-trust',
      name: 'The God of Kicgle',
      tagline: 'Secular Spiritual Harmony, Faith Sanctuary & Universal Food Security',
      color: 'ios-purple',
      status: 'in-process',
      icon: 'sentiment_satisfied_alt',
      services: [
        { icon: 'groups', title: 'Universal Matrimonial Support', desc: 'Full logistical and financial sponsorship for weddings of economically vulnerable couples.' },
        { icon: 'account_balance', title: 'Secular Institutional Harmony', desc: 'Preserving, cleaning, and building peaceful spaces for interfaith meditation and spiritual solace.' },
        { icon: 'sentiment_satisfied_alt', title: 'Emotional & Spiritual Guidance', desc: 'Confidential mental health counseling, trauma recovery programs, and spiritual mindfulness coaching.' },
        { icon: 'redeem', title: 'Universal Free Food Distribution', desc: 'Daily Annadhanam meal kitchens serving fresh, nutritious meals to thousands without question.' },
        { icon: 'house', title: 'Child & Elder Welfare Sanctuaries', desc: 'Modern, safe residential homes providing education, healthcare, and dignified care for children and seniors.' }
      ]
    },
    {
      id: 'kicgle-political-party',
      name: 'Kicgle Political Party',
      tagline: 'Transparent Civic Governance, Anti-Corruption & Tech-Driven Democracy',
      color: 'ios-orange',
      status: 'in-process',
      icon: 'balance',
      services: [
        { icon: 'balance', title: 'Anti-Corruption Taskforce', desc: 'Unrelenting institutional oversight and forensic auditing to eliminate bribery and abuse of public office.' },
        { icon: 'smartphone', title: 'Blockchain-Backed Citizen Voting', desc: 'Tamper-proof mobile referendum platforms allowing citizens to directly vote on municipal and state laws.' },
        { icon: 'groups', title: 'Next-Gen Civic Leadership', desc: 'Intensive ethical leadership fellowships training young visionaries for honest public administration.' },
        { icon: 'forum', title: 'Real-Time Grievance Redressal', desc: 'Transparent public ticket resolution system tracking citizen complaints with binding resolution deadlines.' },
        { icon: 'front_hand', title: 'Human Rights Legal Shield', desc: 'Pro-bono legal defense networks ensuring protection against unjust harassment and civil liberties violations.' }
      ]
    }
  ]
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-about',
  imports: [CommonModule, MatIconModule, RouterModule, Header, SwipeTabSyncDirective],
  template: `
<div class="min-h-screen w-full transition-colors duration-500 relative overflow-x-hidden flex flex-col">
  <!-- Top Navigation Header (Fixed) -->
  <app-header 
    title="About" 
    subtitle="@about" 
    iconName="info">
  </app-header>

  <!-- Pinned Sub-Header for Navigation Tabs (Matching Profile Page Layout) -->
  <div class="ios-sub-header-liquid">
    <div 
      class="chip-scroll-container justify-start sm:justify-center !gap-2.5 sm:!gap-3"
      #aboutChipContainer
    >
      @for (tab of mainTabs; track tab.id) {
        <button
          type="button"
          (click)="selectMainTab(tab.id)"
          [id]="'about-tab-' + tab.id"
          class="ios-liquid-chip !px-4 sm:!px-5 !py-1.5 !rounded-full transition-all duration-300"
          [class.active]="activeMainTab() === tab.id"
          [attr.aria-selected]="activeMainTab() === tab.id"
        >
          <mat-icon class="!text-[16px] sm:!text-[18px]">{{ tab.icon }}</mat-icon>
          <span class="font-bold text-[13px] sm:text-[14px] whitespace-nowrap">{{ tab.label }}</span>
        </button>
      }
    </div>
  </div>

  <!-- Main Scrollable Content (Scrolls cleanly below pinned header & subheader) -->
  <main 
    class="flex-1 w-full relative z-10 pt-[116px] md:pt-[128px] animate-ios-fade pb-24"
    appSwipeTabSync
    [tabs]="mainTabs"
    [activeTab]="activeMainTab()"
    [chipContainer]="aboutChipContainer"
    chipIdPrefix="about-tab-"
    (activeTabChange)="selectMainTab($any($event))"
  >

    <!-- ============================================================== -->
    <!-- TAB 1: ABOUT KICGLE (OVERVIEW & 6 PRINCIPLES OF SUCCESS) -->
    <!-- ============================================================== -->
    @if (activeMainTab() === 'about') {
      <div class="animate-ios-fade">
        <!-- Hero Section -->
        <section class="max-w-4xl mx-auto px-6 text-center pt-10 pb-12 sm:pb-16 relative">
          <!-- Ambient Liquid Glows -->
          <div class="absolute -top-12 left-1/2 -translate-x-1/2 w-80 h-80 bg-ios-blue/15 dark:bg-ios-blue/10 rounded-full blur-3xl pointer-events-none animate-liquid"></div>
          
          <div class="relative z-10 space-y-6">
            <div class="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-ios-blue/10 text-ios-blue dark:text-sky-400 border border-ios-blue/20 text-xs font-bold tracking-wide shadow-sm">
              <mat-icon class="!text-[16px]">verified</mat-icon>
              <span>Global Innovation & Humanitarian Conglomerate</span>
            </div>

            <img 
              src="kicgle.svg" 
              alt="Kicgle" 
              class="h-20 md:h-28 w-auto mx-auto drop-shadow-2xl object-contain hover:scale-105 transition-transform duration-700" 
              referrerpolicy="no-referrer" 
              onerror="this.style.display='none'"
            />

            <h1 class="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-ios-title leading-tight">
              A Global Ecosystem Redefining Technology, Service, Humanity & Governance.
            </h1>

            <p class="text-base sm:text-lg text-gray-600 dark:text-gray-400 font-medium max-w-2xl mx-auto leading-relaxed">
              Kicgle unites revolutionary commercial enterprises, public financial architectures, and global humanitarian trusts into a single cohesive entity committed to lifting human potential.
            </p>

            <!-- Quick Navigation Shortcuts to Limiteds and Trusts -->
            <div class="flex flex-wrap items-center justify-center gap-3 pt-3">
              <button
                type="button"
                (click)="selectMainTab('limiteds')"
                class="px-5 py-2.5 rounded-2xl bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/15 text-gray-900 dark:text-white font-bold text-xs sm:text-sm flex items-center gap-2 transition-all active:scale-95 shadow-sm border border-black/5 dark:border-white/10"
              >
                <mat-icon class="!text-lg text-ios-red">business</mat-icon>
                <span>Explore The Kicgle Limiteds</span>
                <mat-icon class="!text-base opacity-60">arrow_forward</mat-icon>
              </button>

              <button
                type="button"
                (click)="selectMainTab('trusts')"
                class="px-5 py-2.5 rounded-2xl bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/15 text-gray-900 dark:text-white font-bold text-xs sm:text-sm flex items-center gap-2 transition-all active:scale-95 shadow-sm border border-black/5 dark:border-white/10"
              >
                <mat-icon class="!text-lg text-ios-blue">volunteer_activism</mat-icon>
                <span>Explore The Kicgle Trusts</span>
                <mat-icon class="!text-base opacity-60">arrow_forward</mat-icon>
              </button>
            </div>
          </div>
        </section>

        <!-- SECTION: THE 6 PRINCIPLES OF SUCCESS (K-I-C-G-L-E) -->
        <section class="max-w-7xl mx-auto px-6 mb-24">
          <div class="text-center space-y-4 mb-14">
            <h2 class="text-3xl md:text-5xl font-black tracking-tight text-ios-title">The 6 Principles of Success</h2>
            <p class="text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto font-medium tracking-tight">
              Our name, KICGLE, is a powerful six-step methodology for achieving excellence in everything we do.
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <!-- K -->
            <div class="glass-card rounded-[32px] p-8 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-ios-blue/20 transition-all duration-500 border border-ios-blue/15 relative overflow-hidden group">
               <div class="absolute -right-8 -top-8 w-32 h-32 bg-ios-blue/10 rounded-full blur-2xl group-hover:bg-ios-blue/20 transition-colors animate-liquid"></div>
               <div class="w-16 h-16 rounded-[22px] bg-gradient-to-br from-ios-blue/20 to-ios-blue/5 text-ios-blue flex items-center justify-center text-3xl font-black shadow-inner mb-6 relative z-10 border border-ios-blue/20">
                 <mat-icon>search</mat-icon>
               </div>
               <h3 class="text-2xl font-black tracking-tight text-ios-title mb-3 relative z-10 flex items-center gap-2">
                 <span class="text-ios-blue">K</span> - Keen (Learning)
               </h3>
               <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed font-medium relative z-10">
                 Learn deeply and acquire complete knowledge about whatever you do.
               </p>
            </div>

            <!-- I -->
            <div class="glass-card rounded-[32px] p-8 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-ios-red/20 transition-all duration-500 border border-ios-red/15 relative overflow-hidden group">
               <div class="absolute -right-8 -top-8 w-32 h-32 bg-ios-red/10 rounded-full blur-2xl group-hover:bg-ios-red/20 transition-colors animate-liquid"></div>
               <div class="w-16 h-16 rounded-[22px] bg-gradient-to-br from-ios-red/20 to-ios-red/5 text-ios-red flex items-center justify-center text-3xl font-black shadow-inner mb-6 relative z-10 border border-ios-red/20">
                 <mat-icon>emoji_objects</mat-icon>
               </div>
               <h3 class="text-2xl font-black tracking-tight text-ios-title mb-3 relative z-10 flex items-center gap-2">
                 <span class="text-ios-red">I</span> - Idea (Innovation)
               </h3>
               <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed font-medium relative z-10">
                 Think of new ways that make you stand out from the rest.
               </p>
            </div>

            <!-- C -->
            <div class="glass-card rounded-[32px] p-8 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-ios-orange/20 transition-all duration-500 border border-ios-orange/15 relative overflow-hidden group">
               <div class="absolute -right-8 -top-8 w-32 h-32 bg-ios-orange/10 rounded-full blur-2xl group-hover:bg-ios-orange/20 transition-colors animate-liquid"></div>
               <div class="w-16 h-16 rounded-[22px] bg-gradient-to-br from-ios-orange/20 to-ios-orange/5 text-ios-orange flex items-center justify-center text-3xl font-black shadow-inner mb-6 relative z-10 border border-ios-orange/20">
                 <mat-icon>shield</mat-icon>
               </div>
               <h3 class="text-2xl font-black tracking-tight text-ios-title mb-3 relative z-10 flex items-center gap-2">
                 <span class="text-ios-orange">C</span> - Core (Determination)
               </h3>
               <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed font-medium relative z-10">
                 Be unshakably stubborn until you finish the task you started.
               </p>
            </div>

            <!-- G -->
            <div class="glass-card rounded-[32px] p-8 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-ios-purple/20 transition-all duration-500 border border-ios-purple/15 relative overflow-hidden group">
               <div class="absolute -right-8 -top-8 w-32 h-32 bg-ios-purple/10 rounded-full blur-2xl group-hover:bg-ios-purple/20 transition-colors animate-liquid"></div>
               <div class="w-16 h-16 rounded-[22px] bg-gradient-to-br from-ios-purple/20 to-ios-purple/5 text-ios-purple flex items-center justify-center text-3xl font-black shadow-inner mb-6 relative z-10 border border-ios-purple/20">
                 <mat-icon>rocket_launch</mat-icon>
               </div>
               <h3 class="text-2xl font-black tracking-tight text-ios-title mb-3 relative z-10 flex items-center gap-2">
                 <span class="text-ios-purple">G</span> - Go (Speed)
               </h3>
               <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed font-medium relative z-10">
                 Don't just keep thinking; jump into action immediately at lightning speed.
               </p>
            </div>

            <!-- L -->
            <div class="glass-card rounded-[32px] p-8 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-ios-green/20 transition-all duration-500 border border-ios-green/15 relative overflow-hidden group">
               <div class="absolute -right-8 -top-8 w-32 h-32 bg-ios-green/10 rounded-full blur-2xl group-hover:bg-ios-green/20 transition-colors animate-liquid"></div>
               <div class="w-16 h-16 rounded-[22px] bg-gradient-to-br from-ios-green/20 to-ios-green/5 text-ios-green flex items-center justify-center text-3xl font-black shadow-inner mb-6 relative z-10 border border-ios-green/20">
                 <mat-icon>account_tree</mat-icon>
               </div>
               <h3 class="text-2xl font-black tracking-tight text-ios-title mb-3 relative z-10 flex items-center gap-2">
                 <span class="text-ios-green">L</span> - Lead (Planning)
               </h3>
               <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed font-medium relative z-10">
                 Do not get emotional; use your intellect to plan properly.
               </p>
            </div>

            <!-- E -->
            <div class="glass-card rounded-[32px] p-8 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-ios-pink/20 transition-all duration-500 border border-ios-pink/15 relative overflow-hidden group">
               <div class="absolute -right-8 -top-8 w-32 h-32 bg-ios-pink/10 rounded-full blur-2xl group-hover:bg-ios-pink/20 transition-colors animate-liquid"></div>
               <div class="w-16 h-16 rounded-[22px] bg-gradient-to-br from-ios-pink/20 to-ios-pink/5 text-ios-pink flex items-center justify-center text-3xl font-black shadow-inner mb-6 relative z-10 border border-ios-pink/20">
                 <mat-icon>all_inclusive</mat-icon>
               </div>
               <h3 class="text-2xl font-black tracking-tight text-ios-title mb-3 relative z-10 flex items-center gap-2">
                 <span class="text-ios-pink">E</span> - Ever (Quality)
               </h3>
               <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed font-medium relative z-10">
                 Always complete every action you do with uncompromising, excellent quality.
               </p>
            </div>
          </div>

          <!-- Motivational Mantra Callout Box -->
          <div class="mt-10 p-8 md:p-12 rounded-[36px] bg-white/70 dark:bg-zinc-900/80 backdrop-blur-2xl border border-white/60 dark:border-white/10 shadow-2xl text-center relative overflow-hidden group">
            <div class="absolute inset-0 bg-gradient-to-r from-ios-blue/5 via-ios-purple/5 to-ios-pink/5 group-hover:scale-105 transition-transform duration-1000"></div>
            <div class="text-base sm:text-xl md:text-2xl font-black tracking-tight text-ios-title leading-relaxed relative z-10 space-y-2">
              <p>
                Learn clearly (<span class="text-ios-blue font-black">Keen</span>), Think innovatively (<span class="text-ios-red font-black">Idea</span>)
              </p>
              <p>
                Be determinate (<span class="text-ios-orange font-black">Core</span>), Act fast (<span class="text-ios-purple font-black">Go</span>), Plan properly (<span class="text-ios-green font-black">Lead</span>)
              </p>
              <p>
                Deliver everlasting quality (<span class="text-ios-pink font-black">Ever</span>) - 
                <span class="text-amber-500 dark:text-amber-400 uppercase tracking-wider underline decoration-amber-400 decoration-2 underline-offset-4 ml-1">
                  SUCCESS IS CERTAIN
                </span>
              </p>
            </div>
          </div>
        </section>

        <!-- SECTION: CONGLOMERATE STRUCTURE SUMMARY -->
        <section class="max-w-7xl mx-auto px-6 mb-24">
          <div class="text-center space-y-3 mb-12">
            <h2 class="text-3xl md:text-4xl font-black tracking-tight text-ios-title">Two Pillars of Impact</h2>
            <p class="text-base text-gray-500 dark:text-gray-400 max-w-xl mx-auto font-medium">
              A balanced architecture uniting sustainable commercial power with non-profit humanitarian service.
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <!-- Pillar 1: The Kicgle Limiteds -->
            <div class="glass-card rounded-[36px] p-8 md:p-10 border border-ios-red/20 hover:border-ios-red/40 transition-all duration-300 relative overflow-hidden flex flex-col justify-between">
              <div class="space-y-4">
                <div class="w-14 h-14 rounded-2xl bg-ios-red/10 text-ios-red flex items-center justify-center">
                  <mat-icon class="!text-3xl">business</mat-icon>
                </div>
                <h3 class="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight">The Kicgle Limiteds</h3>
                <p class="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
                  3 commercial entities spearheading quantum computing, global banking, aerospace, semiconductor manufacturing, and public enterprise infrastructure.
                </p>
                <div class="flex flex-wrap gap-2 pt-2">
                  <span class="px-3 py-1 rounded-full text-xs font-bold bg-ios-red/10 text-ios-red">Kicgle Private Ltd</span>
                  <span class="px-3 py-1 rounded-full text-xs font-bold bg-ios-green/10 text-ios-green">Kicgle Bank Ltd</span>
                  <span class="px-3 py-1 rounded-full text-xs font-bold bg-ios-pink/10 text-ios-pink">Kicgle Public Ltd</span>
                </div>
              </div>
              <div class="pt-8">
                <button
                  type="button"
                  (click)="selectMainTab('limiteds')"
                  class="w-full py-3.5 px-6 rounded-2xl bg-ios-red hover:bg-ios-red/90 text-white font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-95 shadow-md shadow-ios-red/25"
                >
                  <span>Explore 3 Limited Entities</span>
                  <mat-icon class="!text-lg">arrow_forward</mat-icon>
                </button>
              </div>
            </div>

            <!-- Pillar 2: The Kicgle Trusts -->
            <div class="glass-card rounded-[36px] p-8 md:p-10 border border-ios-blue/20 hover:border-ios-blue/40 transition-all duration-300 relative overflow-hidden flex flex-col justify-between">
              <div class="space-y-4">
                <div class="w-14 h-14 rounded-2xl bg-ios-blue/10 text-ios-blue flex items-center justify-center">
                  <mat-icon class="!text-3xl">volunteer_activism</mat-icon>
                </div>
                <h3 class="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight">The Kicgle Trusts</h3>
                <p class="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
                  3 non-profit entities directing universal healthcare, earth restoration, spiritual harmony, daily food relief, and unyielding civic transparency.
                </p>
                <div class="flex flex-wrap gap-2 pt-2">
                  <span class="px-3 py-1 rounded-full text-xs font-bold bg-ios-blue/10 text-ios-blue">Kicgle Organization</span>
                  <span class="px-3 py-1 rounded-full text-xs font-bold bg-ios-purple/10 text-ios-purple">The God of Kicgle</span>
                  <span class="px-3 py-1 rounded-full text-xs font-bold bg-ios-orange/10 text-ios-orange">Kicgle Political Party</span>
                </div>
              </div>
              <div class="pt-8">
                <button
                  type="button"
                  (click)="selectMainTab('trusts')"
                  class="w-full py-3.5 px-6 rounded-2xl bg-ios-blue hover:bg-ios-blue/90 text-white font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-95 shadow-md shadow-ios-blue/25"
                >
                  <span>Explore 3 Trust Entities</span>
                  <mat-icon class="!text-lg">arrow_forward</mat-icon>
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- Contact Section -->
        <section class="max-w-7xl mx-auto px-6 border-t border-black/5 dark:border-white/10 pt-16">
          <div class="flex flex-col md:flex-row gap-10 justify-between items-center">
            <div class="text-center md:text-left space-y-2">
              <h2 class="text-3xl md:text-5xl font-black tracking-tight text-ios-title">Get in Touch</h2>
              <p class="text-base sm:text-lg text-gray-500 dark:text-gray-400 font-medium">
                Connect with the Kicgle Global Secretariat.
              </p>
            </div>
            
            <div class="flex flex-wrap justify-center gap-3 text-sm font-bold">
               <a href="tel:+918056774620" class="flex items-center gap-3 bg-white/60 dark:bg-zinc-800/60 hover:bg-white/80 dark:hover:bg-zinc-700/80 p-2.5 pr-6 rounded-full shadow-sm hover:shadow-md transition-all border border-black/5 dark:border-white/10 group backdrop-blur-md">
                 <div class="w-10 h-10 rounded-full bg-gradient-to-br from-ios-blue to-blue-600 flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform"><mat-icon class="!text-lg">phone</mat-icon></div>
                 <span>+91-80567 74620</span>
               </a>
               <a href="https://kicgle.com" target="_blank" class="flex items-center gap-3 bg-white/60 dark:bg-zinc-800/60 hover:bg-white/80 dark:hover:bg-zinc-700/80 p-2.5 pr-6 rounded-full shadow-sm hover:shadow-md transition-all border border-black/5 dark:border-white/10 group backdrop-blur-md">
                 <div class="w-10 h-10 rounded-full bg-gradient-to-br from-ios-blue to-blue-600 flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform"><mat-icon class="!text-lg">language</mat-icon></div>
                 <span>kicgle.com</span>
               </a>
               <a href="mailto:e@kicgle.com" class="flex items-center gap-3 bg-white/60 dark:bg-zinc-800/60 hover:bg-white/80 dark:hover:bg-zinc-700/80 p-2.5 pr-6 rounded-full shadow-sm hover:shadow-md transition-all border border-black/5 dark:border-white/10 group backdrop-blur-md">
                 <div class="w-10 h-10 rounded-full bg-gradient-to-br from-ios-blue to-blue-600 flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform"><mat-icon class="!text-lg">email</mat-icon></div>
                 <span>e@kicgle.com</span>
               </a>
            </div>
          </div>
        </section>
      </div>
    }

    <!-- ============================================================== -->
    <!-- TAB 2: THE KICGLE LIMITEDS (PROFIT ENTITIES) -->
    <!-- ============================================================== -->
    @if (activeMainTab() === 'limiteds') {
      <div class="animate-ios-fade max-w-[1400px] mx-auto px-6 pt-6">
        <!-- Tab Header -->
        <div class="text-center space-y-4 mb-10">
          <div class="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-ios-red/10 text-ios-red border border-ios-red/20 text-xs font-bold tracking-wide shadow-sm">
            <mat-icon class="!text-[16px]">business</mat-icon>
            <span>Commercial & Deep-Tech Powerhouse</span>
          </div>
          <h2 class="text-3xl sm:text-4xl md:text-6xl font-black tracking-tight text-ios-title">The Kicgle Limiteds</h2>
          <p class="text-base sm:text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto font-medium tracking-tight">
            3 distinct profit entities driving quantum compute, space exploration, digital banking, and sovereign infrastructure.
          </p>
        </div>

        <!-- Entity Selector Chips -->
        <div class="flex flex-wrap justify-center items-center gap-2.5 sm:gap-3 mb-10">
          @for (entity of limitedEntities; track entity.id) {
            <button 
              type="button"
              (click)="selectedLimitedEntityId.set(entity.id)" 
              class="px-5 py-2.5 rounded-full font-bold text-xs sm:text-sm transition-all duration-300 flex items-center gap-2.5 shadow-sm border"
              [class.bg-ios-red]="selectedLimitedEntityId() === entity.id && entity.color === 'ios-red'"
              [class.bg-ios-green]="selectedLimitedEntityId() === entity.id && entity.color === 'ios-green'"
              [class.bg-ios-pink]="selectedLimitedEntityId() === entity.id && entity.color === 'ios-pink'"
              [class.text-white]="selectedLimitedEntityId() === entity.id"
              [class.border-transparent]="selectedLimitedEntityId() === entity.id"
              [class.shadow-md]="selectedLimitedEntityId() === entity.id"
              [class.bg-white/70]="selectedLimitedEntityId() !== entity.id"
              [class.dark:bg-zinc-800/70]="selectedLimitedEntityId() !== entity.id"
              [class.text-gray-700]="selectedLimitedEntityId() !== entity.id"
              [class.dark:text-zinc-300]="selectedLimitedEntityId() !== entity.id"
              [class.border-black/5]="selectedLimitedEntityId() !== entity.id"
              [class.dark:border-white/10]="selectedLimitedEntityId() !== entity.id"
              [class.hover:bg-white]="selectedLimitedEntityId() !== entity.id"
            >
              <mat-icon class="!text-lg">{{ entity.icon }}</mat-icon>
              <span>{{ entity.name }}</span>
              @if (entity.status === 'active') {
                <span class="text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider"
                      [class.bg-white/25]="selectedLimitedEntityId() === entity.id"
                      [class.text-white]="selectedLimitedEntityId() === entity.id"
                      [class.bg-emerald-100]="selectedLimitedEntityId() !== entity.id"
                      [class.text-emerald-700]="selectedLimitedEntityId() !== entity.id">
                  Active
                </span>
              } @else {
                <span class="text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider"
                      [class.bg-white/25]="selectedLimitedEntityId() === entity.id"
                      [class.text-white]="selectedLimitedEntityId() === entity.id"
                      [class.bg-amber-100]="selectedLimitedEntityId() !== entity.id"
                      [class.text-amber-700]="selectedLimitedEntityId() !== entity.id">
                  Pipeline
                </span>
              }
            </button>
          }
        </div>

        <!-- Entity Banner Card -->
        <div class="glass-card rounded-[32px] p-6 sm:p-8 mb-10 border border-black/5 dark:border-white/10 flex flex-col sm:flex-row items-center sm:items-start gap-5 relative overflow-hidden">
          <div 
            class="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 text-white shadow-lg"
            [class.bg-ios-red]="currentLimitedEntity().color === 'ios-red'"
            [class.bg-ios-green]="currentLimitedEntity().color === 'ios-green'"
            [class.bg-ios-pink]="currentLimitedEntity().color === 'ios-pink'"
          >
            <mat-icon class="!text-3xl">{{ currentLimitedEntity().icon }}</mat-icon>
          </div>
          <div class="text-center sm:text-left flex-1 min-w-0">
            <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2.5 mb-1.5">
              <h3 class="text-xl sm:text-2xl font-black text-gray-900 dark:text-white tracking-tight">
                {{ currentLimitedEntity().name }}
              </h3>
              @if (currentLimitedEntity().status === 'active') {
                <span class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                  <mat-icon class="!text-xs">check_circle</mat-icon> Active Commercial Entity
                </span>
              } @else {
                <span class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 flex items-center gap-1">
                  <mat-icon class="!text-xs">pending</mat-icon> In Process - Global Pipeline
                </span>
              }
            </div>
            <p class="text-sm sm:text-base text-gray-600 dark:text-gray-300 font-medium">
              {{ currentLimitedEntity().tagline }}
            </p>
          </div>
        </div>

        <!-- SERVICES GRID -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-16">
          @for (service of currentLimitedEntity().services; track service.title) {
            <div class="glass-card p-6 md:p-8 rounded-[32px] border border-black/5 dark:border-white/10 hover:-translate-y-1.5 hover:shadow-2xl transition-all duration-300 group flex flex-col justify-between">
              <div>
                <div 
                  class="w-14 h-14 rounded-2xl shadow-inner mb-6 relative z-10 border flex items-center justify-center text-3xl font-black transition-transform group-hover:scale-110"
                  [class]="'from-' + currentLimitedEntity().color + '/20 to-' + currentLimitedEntity().color + '/5 text-' + currentLimitedEntity().color + ' border-' + currentLimitedEntity().color + '/20 bg-gradient-to-br'"
                >
                  <mat-icon>{{ service.icon }}</mat-icon>
                </div>
                <h4 class="text-lg font-black text-ios-title leading-tight mb-2.5">{{ service.title }}</h4>
                <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
                  {{ service.desc }}
                </p>
              </div>
              <div class="pt-6 flex items-center gap-1.5 text-xs font-bold" [class]="'text-' + currentLimitedEntity().color">
                <span>Enterprise Service</span>
                <mat-icon class="!text-sm">arrow_forward</mat-icon>
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- ============================================================== -->
    <!-- TAB 3: THE KICGLE TRUSTS (NON-PROFIT ENTITIES) -->
    <!-- ============================================================== -->
    @if (activeMainTab() === 'trusts') {
      <div class="animate-ios-fade max-w-[1400px] mx-auto px-6 pt-6">
        <!-- Tab Header -->
        <div class="text-center space-y-4 mb-10">
          <div class="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-ios-blue/10 text-ios-blue border border-ios-blue/20 text-xs font-bold tracking-wide shadow-sm">
            <mat-icon class="!text-[16px]">volunteer_activism</mat-icon>
            <span>Humanitarian & Civic Foundation</span>
          </div>
          <h2 class="text-3xl sm:text-4xl md:text-6xl font-black tracking-tight text-ios-title">The Kicgle Trusts</h2>
          <p class="text-base sm:text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto font-medium tracking-tight">
            3 dedicated non-profit entities committed to free healthcare, planetary restoration, universal nutrition, and ethical democratic governance.
          </p>
        </div>

        <!-- Entity Selector Chips -->
        <div class="flex flex-wrap justify-center items-center gap-2.5 sm:gap-3 mb-10">
          @for (entity of trustEntities; track entity.id) {
            <button 
              type="button"
              (click)="selectedTrustEntityId.set(entity.id)" 
              class="px-5 py-2.5 rounded-full font-bold text-xs sm:text-sm transition-all duration-300 flex items-center gap-2.5 shadow-sm border"
              [class.bg-ios-blue]="selectedTrustEntityId() === entity.id && entity.color === 'ios-blue'"
              [class.bg-ios-purple]="selectedTrustEntityId() === entity.id && entity.color === 'ios-purple'"
              [class.bg-ios-orange]="selectedTrustEntityId() === entity.id && entity.color === 'ios-orange'"
              [class.text-white]="selectedTrustEntityId() === entity.id"
              [class.border-transparent]="selectedTrustEntityId() === entity.id"
              [class.shadow-md]="selectedTrustEntityId() === entity.id"
              [class.bg-white/70]="selectedTrustEntityId() !== entity.id"
              [class.dark:bg-zinc-800/70]="selectedTrustEntityId() !== entity.id"
              [class.text-gray-700]="selectedTrustEntityId() !== entity.id"
              [class.dark:text-zinc-300]="selectedTrustEntityId() !== entity.id"
              [class.border-black/5]="selectedTrustEntityId() !== entity.id"
              [class.dark:border-white/10]="selectedTrustEntityId() !== entity.id"
              [class.hover:bg-white]="selectedTrustEntityId() !== entity.id"
            >
              <mat-icon class="!text-lg">{{ entity.icon }}</mat-icon>
              <span>{{ entity.name }}</span>
              @if (entity.status === 'active') {
                <span class="text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider"
                      [class.bg-white/25]="selectedTrustEntityId() === entity.id"
                      [class.text-white]="selectedTrustEntityId() === entity.id"
                      [class.bg-emerald-100]="selectedTrustEntityId() !== entity.id"
                      [class.text-emerald-700]="selectedTrustEntityId() !== entity.id">
                  Active
                </span>
              } @else {
                <span class="text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider"
                      [class.bg-white/25]="selectedTrustEntityId() === entity.id"
                      [class.text-white]="selectedTrustEntityId() === entity.id"
                      [class.bg-amber-100]="selectedTrustEntityId() !== entity.id"
                      [class.text-amber-700]="selectedTrustEntityId() !== entity.id">
                  Pipeline
                </span>
              }
            </button>
          }
        </div>

        <!-- Entity Banner Card -->
        <div class="glass-card rounded-[32px] p-6 sm:p-8 mb-10 border border-black/5 dark:border-white/10 flex flex-col sm:flex-row items-center sm:items-start gap-5 relative overflow-hidden">
          <div 
            class="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 text-white shadow-lg"
            [class.bg-ios-blue]="currentTrustEntity().color === 'ios-blue'"
            [class.bg-ios-purple]="currentTrustEntity().color === 'ios-purple'"
            [class.bg-ios-orange]="currentTrustEntity().color === 'ios-orange'"
          >
            <mat-icon class="!text-3xl">{{ currentTrustEntity().icon }}</mat-icon>
          </div>
          <div class="text-center sm:text-left flex-1 min-w-0">
            <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2.5 mb-1.5">
              <h3 class="text-xl sm:text-2xl font-black text-gray-900 dark:text-white tracking-tight">
                {{ currentTrustEntity().name }}
              </h3>
              @if (currentTrustEntity().status === 'active') {
                <span class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                  <mat-icon class="!text-xs">check_circle</mat-icon> Active Humanitarian Trust
                </span>
              } @else {
                <span class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 flex items-center gap-1">
                  <mat-icon class="!text-xs">pending</mat-icon> In Process - Global Pipeline
                </span>
              }
            </div>
            <p class="text-sm sm:text-base text-gray-600 dark:text-gray-300 font-medium">
              {{ currentTrustEntity().tagline }}
            </p>
          </div>
        </div>

        <!-- SERVICES GRID -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 pb-16">
          @for (service of currentTrustEntity().services; track service.title) {
            <div class="glass-card p-6 md:p-8 rounded-[32px] border border-black/5 dark:border-white/10 hover:-translate-y-1.5 hover:shadow-2xl transition-all duration-300 group flex flex-col justify-between">
              <div>
                <div 
                  class="w-14 h-14 rounded-2xl shadow-inner mb-6 relative z-10 border flex items-center justify-center text-3xl font-black transition-transform group-hover:scale-110"
                  [class]="'from-' + currentTrustEntity().color + '/20 to-' + currentTrustEntity().color + '/5 text-' + currentTrustEntity().color + ' border-' + currentTrustEntity().color + '/20 bg-gradient-to-br'"
                >
                  <mat-icon>{{ service.icon }}</mat-icon>
                </div>
                <h4 class="text-lg font-black text-ios-title leading-tight mb-2.5">{{ service.title }}</h4>
                <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
                  {{ service.desc }}
                </p>
              </div>
              <div class="pt-6 flex items-center gap-1.5 text-xs font-bold" [class]="'text-' + currentTrustEntity().color">
                <span>Humanitarian Mandate</span>
                <mat-icon class="!text-sm">favorite</mat-icon>
              </div>
            </div>
          }
        </div>
      </div>
    }

  </main>
</div>
  `,
})
export class AboutView implements OnInit {
  authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  mainTabs: MainTabOption[] = [
    { id: 'about', label: 'About', icon: 'info', badge: 'Overview' },
    { id: 'limiteds', label: 'The Kicgle Limiteds', icon: 'business', badge: '3 Entities' },
    { id: 'trusts', label: 'The Kicgle Trusts', icon: 'volunteer_activism', badge: '3 Entities' },
  ];

  activeMainTab = signal<AboutMainTab>('about');

  limitedEntities = KICGLE_SERVICES_DATA.limiteds;
  trustEntities = KICGLE_SERVICES_DATA.trusts;

  selectedLimitedEntityId = signal<string>('kicgle-private-limited');
  selectedTrustEntityId = signal<string>('kicgle-organization-trust');

  currentLimitedEntity = computed(() => {
    const list = this.limitedEntities;
    return list.find(e => e.id === this.selectedLimitedEntityId()) || list[0];
  });

  currentTrustEntity = computed(() => {
    const list = this.trustEntities;
    return list.find(e => e.id === this.selectedTrustEntityId()) || list[0];
  });

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const tabParam = params['tab']?.toLowerCase();
      if (tabParam === 'limiteds' || tabParam === 'the-kicgle-limiteds') {
        this.activeMainTab.set('limiteds');
      } else if (tabParam === 'trusts' || tabParam === 'the-kicgle-trusts') {
        this.activeMainTab.set('trusts');
      } else if (tabParam === 'about') {
        this.activeMainTab.set('about');
      }
    });
  }

  selectMainTab(tab: AboutMainTab) {
    this.activeMainTab.set(tab);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab === 'about' ? null : tab },
      queryParamsHandling: 'merge',
    });
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
